import { cva } from "cva";
import { type ReactNode } from "react";
import {
  Dialog,
  DialogTrigger,
  Modal,
  ModalOverlay,
  type ModalOverlayProps,
} from "react-aria-components";

const overlayStyles = cva({
  base: "fixed inset-0 z-50 backdrop-blur-md",
  variants: {
    isEntering: {
      true: "animate-in fade-in duration-200",
    },
    isExiting: {
      true: "animate-out fade-out duration-200",
    },
  },
});

const modalStyles = cva({
  base: "fixed z-50 shadow-2xl outline-none bg-theme-background",
  variants: {
    side: {
      right: "inset-y-0 right-0 w-full max-w-md border-l",
      left: "inset-y-0 left-0 w-full max-w-md",
      top: "inset-x-0 top-0 max-h-[80vh]",
      bottom: "inset-x-0 bottom-0 max-h-[90vh] h-[90vh] rounded-t-2xl",
    },
    isEntering: {
      true: "",
    },
    isExiting: {
      true: "",
    },
  },
  compoundVariants: [
    {
      side: "right",
      isEntering: true,
      className: "animate-in slide-in-from-right duration-300",
    },

    {
      side: "left",
      isEntering: true,
      className: "animate-in slide-in-from-left duration-300",
    },

    {
      side: "top",
      isEntering: true,
      className: "animate-in slide-in-from-top duration-300",
    },

    {
      side: "bottom",
      isEntering: true,
      className: "animate-in slide-in-from-bottom duration-300",
    },
  ],
  defaultVariants: {
    side: "right",
  },
});

const dialogStyles = cva({
  base: "flex h-full flex-col outline-none",
});

const bodyStyles = cva({
  base: "flex-1 overflow-y-auto p-6",
});

type SheetSide = "top" | "right" | "bottom" | "left";

interface SheetContentProps extends Omit<ModalOverlayProps, "className"> {
  children: ReactNode;
  side?: SheetSide;
  className?: string;
}

interface SheetChildProps {
  children: ReactNode;
  className?: string;
}

const Sheet = DialogTrigger;

function SheetContent({
  children,
  side = "right",
  className,
  ...props
}: SheetContentProps) {
  return (
    <ModalOverlay
      className={({ isEntering, isExiting }) =>
        overlayStyles({ isEntering, isExiting })
      }
      {...props}
    >
      <Modal
        className={({ isEntering, isExiting }) =>
          modalStyles({ side, isEntering, isExiting, className })
        }
      >
        <Dialog className={dialogStyles()}>{children}</Dialog>
      </Modal>
    </ModalOverlay>
  );
}

function SheetBody({ children, className }: SheetChildProps) {
  return <div className={bodyStyles({ className })}>{children}</div>;
}

export {
  Sheet,
  SheetBody,
  SheetContent,
  type SheetContentProps,
  type SheetSide,
};
